import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { APP_NAME } from '../config/index.js';
import { SIGN_IN_PAGE_ID, useSignOut } from './AccountChrome.jsx';
import { LanguagePicker, useLanguage } from '../i18n/LanguageContext.jsx';
import HelpDrawer from '../help/HelpDrawer.jsx';
import { DEFAULT_PAGE_ID, getPageById, routeForPageId } from './menuData.js';

// Auth pages that draw their own brand row and framing panel; the shell header would only duplicate it.
const SELF_BRANDED_PATHS = new Set(['/sign-in', '/user-registration', '/forgot-password', '/reset-password']);

/** Minimal shell for public (anonymous) pages: brand + session control + content. */
export default function AnonymousShell({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  const signOut = useSignOut();
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const signInRoute = routeForPageId(SIGN_IN_PAGE_ID);
  const onSignIn = pathname === '/' + getPageById(SIGN_IN_PAGE_ID).slug;

  if (SELF_BRANDED_PATHS.has(pathname)) {
    return <main id="main-content">{children ?? <Outlet />}</main>;
  }

  return (
    <>
      <header className="d-flex align-items-center justify-content-between px-3 py-2 bg-white border-bottom">
        <Link
          to={routeForPageId(DEFAULT_PAGE_ID)}
          className="fw-bold text-decoration-none"
          style={{ color: 'var(--color-primary)' }}
        >
          {APP_NAME}
        </Link>
        <div className="d-flex align-items-center gap-2">
          <span className="text-primary">
            <HelpDrawer />
          </span>
          <LanguagePicker id="language-picker-anonymous" className="form-select form-select-sm w-auto" />
          {!loading &&
            (isAuthenticated ? (
              <div className="d-flex align-items-center gap-2 small">
                <span className="text-body-secondary">{user.name || user.email}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={signOut}
                >
                  <i className="fas fa-sign-out-alt me-1" aria-hidden="true"></i>{t('Sign out')}
                </button>
              </div>
            ) : (
              !onSignIn && (
                <Link to={signInRoute} className="btn btn-sm btn-outline-primary">
                  <i className="fas fa-sign-in-alt me-1" aria-hidden="true"></i>{t('Log in')}
                </Link>
              )
            ))}
        </div>
      </header>
      <main id="main-content">{children ?? <Outlet />}</main>
    </>
  );
}
